// src/store/slices/branchSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Branch, getOrganizationBranches, getBranchById, insertBranch, updateBranch, deleteBranch, supabase } from '../../utils/supabaseClient';
import { BranchCounts, ApiResponse } from '../../types/custom';
import { Database } from '../../types/supabase';

export interface BranchState {
	branches: Branch[];
	currentBranch: Branch | null;
	branchCounts: BranchCounts | null;
	isLoading: boolean;
	isCreating: boolean;
	isUpdating: boolean;
	isDeleting: boolean;
	isPublishing: boolean;
	hasChanges: boolean;
	error: string | null;
}

const initialState: BranchState = {
	branches: [],
	currentBranch: null,
	branchCounts: null,
	isLoading: false,
	isCreating: false,
	isUpdating: false,
	isDeleting: false,
	isPublishing: false,
	hasChanges: false,
	error: null,
};

// Async thunks
export const fetchBranches = createAsyncThunk(
	'branch/fetchBranches',
	async (organizationId: string) => {
		const { data, error } = await getOrganizationBranches(organizationId);
		if (error) throw error;
		return data || [];
	}
);

export const fetchBranchById = createAsyncThunk(
	'branch/fetchBranchById',
	async ({ branchId, organizationId }: { branchId: string, organizationId: string }) => {
		const branch = await getBranchById(branchId, organizationId);
		if (!branch) throw new Error('Branch not found');
		return branch;
	}
);

export const fetchBranchCounts = createAsyncThunk(
	'branch/fetchBranchCounts',
	async (branchId: string) => {
		const { data, error } = await supabase.rpc('get_branch_counts', {
			p_branch_id: branchId
		});
		if (error) throw error;
		return data as unknown as BranchCounts;
	}
);

export const createNewBranch = createAsyncThunk(
	'branch/createNewBranch',
	async (branchData: Database["public"]["Functions"]["insert_branch"]["Args"]["payload"]) => {
		const result = await insertBranch(branchData);
		if (!result) throw new Error('Null result')

		// Type the result properly
		const typedResult = result as { success: boolean; message?: string; data?: Branch };
		if (!typedResult.success) throw new Error(typedResult.message)
		if (!typedResult.data) throw new Error('No data returned');

		return typedResult.data;
	}
);

export const updateBranchData = createAsyncThunk(
	'branch/updateBranchData',
	async (branchData: Database['public']['Functions']['update_branch']['Args']['payload']) => {
		const result = await updateBranch(branchData);
		if (!result) throw new Error('Null result')

		// Type the result properly
		const typedResult = result as { success: boolean; message?: string; data?: Branch };
		if (!typedResult.success) throw new Error(typedResult.message)
		if (!typedResult.data) throw new Error('No data returned');

		return typedResult.data;
	}
);

export const deleteBranchData = createAsyncThunk(
	'branch/deleteBranchData',
	async (branchId: string) => {
		const { data, error } = await deleteBranch(branchId);
		if (error) throw error;
		return data;
	}
);

export const cloneBranch = createAsyncThunk(
	'branch/cloneBranch',
	async (branchId: string) => {
		const { data, error } = await supabase.rpc('clone_branch' as any, {
			payload: {
				branch_id: branchId
			}
		});
		if (error) throw error;
		
		const response = data as unknown as { success: boolean; new_branch_id?: string; message?: string };
		if (!response.success) {
			throw new Error(response.message || 'Failed to clone branch');
		}
		
		return response.new_branch_id;
	}
);

export const publishBranch = createAsyncThunk(
	'branch/publishBranch',
	async (params: { branchId: string, organizationId: string }) => {
		const { data, error } = await supabase.rpc('publish_menu', {
			p_branch_id: params.branchId,
			p_organization_id: params.organizationId
		});
		if (error) throw error;
		if (!data) throw new Error('No data returned');

		// Type the result properly
		const typedResult = data as unknown as ApiResponse;
		if (!typedResult.success) throw new Error(typedResult.message);

		return typedResult;
	}
);

const branchSlice = createSlice({
	name: 'branch',
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearBranches: (state) => {
			state.branches = [];
			state.branchCounts = null
		},
		setCurrentBranch: (state, action: PayloadAction<Branch | null>) => {
			// @ts-ignore - Avoiding Redux Toolkit deep instantiation issue
			state.currentBranch = action.payload;
		},
		clearCurrentBranch: (state) => {
			state.currentBranch = null;
			state.branchCounts = null;
			state.hasChanges = false;
		},
		setHasChanges: (state, action: PayloadAction<boolean>) => {
			state.hasChanges = action.payload;
		}
	},
	extraReducers: (builder) => {
		builder
			// Fetch branches
			.addCase(fetchBranches.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchBranches.fulfilled, (state, action) => {
				state.isLoading = false;
				state.branches = action.payload;
				state.error = null;
			})
			.addCase(fetchBranches.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to fetch branches';
			})
			// Fetch branch by ID
			.addCase(fetchBranchById.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchBranchById.fulfilled, (state, action) => {
				state.isLoading = false;
				state.currentBranch = action.payload as Branch;
				state.hasChanges = false;
				state.error = null;
			})
			.addCase(fetchBranchById.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to fetch branch';
			})
			// Fetch branch counts
			.addCase(fetchBranchCounts.pending, (state) => {
				// Don't set loading for counts as it's secondary data
			})
			.addCase(fetchBranchCounts.fulfilled, (state, action) => {
				state.branchCounts = action.payload as BranchCounts;
			})
			.addCase(fetchBranchCounts.rejected, (state, action) => {
				console.error('Failed to fetch branch counts:', action.error.message);
			})
			// Create branch
			.addCase(createNewBranch.pending, (state) => {
				state.isCreating = true;
				state.error = null;
			})
			.addCase(createNewBranch.fulfilled, (state, action) => {
				state.isCreating = false;
				state.branches.unshift(action.payload);
				state.error = null;
			})
			.addCase(createNewBranch.rejected, (state, action) => {
				state.isCreating = false;
				state.error = action.error.message || 'Failed to create branch';
			})
			// Update branch
			.addCase(updateBranchData.pending, (state) => {
				state.isUpdating = true;
				state.error = null;
			})
			.addCase(updateBranchData.fulfilled, (state, action) => {
				state.isUpdating = false;
				const index = state.branches.findIndex(b => b.id === action.payload.id);
				if (index !== -1) {
					state.branches[index] = action.payload;
				}
				if (state.currentBranch?.id === action.payload.id) {
					state.currentBranch = action.payload;
				}
				state.hasChanges = true;
				state.error = null;
			})
			.addCase(updateBranchData.rejected, (state, action) => {
				state.isUpdating = false;
				state.error = action.error.message || 'Failed to update branch';
			})
			// Delete branch
			.addCase(deleteBranchData.pending, (state) => {
				state.isDeleting = true;
				state.error = null;
			})
			.addCase(deleteBranchData.fulfilled, (state, action) => {
				state.isDeleting = false;
				state.branches = state.branches.filter(b => b.id !== action.payload.id);
				if (state.currentBranch?.id === action.payload.id) {
					state.currentBranch = null;
					state.branchCounts = null;
				}
				state.error = null;
			})
			.addCase(deleteBranchData.rejected, (state, action) => {
				state.isDeleting = false;
				state.error = action.error.message || 'Failed to delete branch';
			})
			// Clone branch
			.addCase(cloneBranch.pending, (state) => {
				state.isCreating = true;
				state.error = null;
			})
			.addCase(cloneBranch.fulfilled, (state) => {
				state.isCreating = false;
				// Branches will be refreshed separately via fetchBranches
				state.error = null;
			})
			.addCase(cloneBranch.rejected, (state, action) => {
				state.isCreating = false;
				state.error = action.error.message || 'Failed to clone branch';
			})
			// Publish branch
			.addCase(publishBranch.pending, (state) => {
				state.isPublishing = true;
				state.error = null;
			})
			.addCase(publishBranch.fulfilled, (state) => {
				state.isPublishing = false;
				state.hasChanges = false;
				if (state.currentBranch) {
					state.currentBranch.has_changes = false;
				}
				state.error = null;
			})
			.addCase(publishBranch.rejected, (state, action) => {
				state.isPublishing = false;
				state.error = action.error.message || 'Failed to publish branch';
			});
	},
});

export const { clearError, clearBranches, setCurrentBranch, clearCurrentBranch, setHasChanges } = branchSlice.actions;
export default branchSlice.reducer;